import usePrivateAxios from '@/common/api/usePrivateAxios';
import { ErrorResponse } from '@/common/types/errorResponse';
import { extractDateTime, toIsoFromDateTimeParts } from '@/common/utils/dateUtils';
import { AuctionEntryUpdateDatesValidationSchema } from '@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/schemas/auctionEntryUpdateDates.schema';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';

interface UseMaintenanceEstablishmentChoiceAuctionEntryUpdateDatesProps {
  establishmentChoiceId: string | undefined;
  startAt: string | null | undefined;
  finishAt: string | null | undefined;
  options?: Partial<Omit<UseMutationOptions<
    unknown,
    ErrorResponse,
    AuctionEntryUpdateDatesValidationSchema
  >, 'mutationKey' | 'mutationFn'>>;
}

export const useMaintenanceEstablishmentChoiceAuctionEntryUpdateDates = ({
  establishmentChoiceId,
  startAt,
  finishAt,
  options,
}: UseMaintenanceEstablishmentChoiceAuctionEntryUpdateDatesProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdateDates', establishmentChoiceId],
    mutationFn: (form: AuctionEntryUpdateDatesValidationSchema) => {
      const payload: { start_at?: string; finish_at?: string } = {};

      const startChanged = form.start_at_date !== extractDateTime(startAt, 'date')
        || form.start_at_time !== extractDateTime(startAt, 'time');

      const finishChanged = form.finish_at_date !== extractDateTime(finishAt, 'date')
        || form.finish_at_time !== extractDateTime(finishAt, 'time');

      if (startChanged && form.start_at_date && form.start_at_time)
        payload.start_at = toIsoFromDateTimeParts(form.start_at_date, form.start_at_time);

      if (finishChanged && form.finish_at_date && form.finish_at_time)
        payload.finish_at = toIsoFromDateTimeParts(form.finish_at_date, form.finish_at_time);

      return privateAxios.put(
        `/maintenance/establishment_choice/auction_entries/${establishmentChoiceId}/update_dates`,
        { auction_entry: payload },
      );
    },
    ...options,
  });
};
